import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HISTORY_COLUMNS,
  HISTORY_FILTER_CLEAR_COLUMN,
  HISTORY_FILTER_CLOSE_LABEL,
  HISTORY_FILTER_DONE,
  HISTORY_FILTER_EYEBROW,
  HISTORY_FILTER_SEARCH_PLACEHOLDER,
  HISTORY_FILTER_SELECT_ALL,
} from '../constants';
import {
  FilterCheck,
  FilterClose,
  FilterCount,
  FilterDone,
  FilterEyebrow,
  FilterLink,
  FilterPopover as FilterPopoverRoot,
  FilterPopoverFoot,
  FilterPopoverHead,
  FilterPopoverTitle,
  FilterSearch,
  FilterValueLabel,
  FilterValues,
} from './styled';
import type { HistoryFilterColumn, HistoryFilterValue } from './types';

interface FilterPopoverProps {
  column: HistoryFilterColumn;
  anchorEl: HTMLElement;
  values: HistoryFilterValue[];
  deselected: Set<string>;
  onToggle: (value: string, checked: boolean) => void;
  onSetVisible: (values: string[], checked: boolean) => void;
  onClearColumn: () => void;
  onClose: () => void;
}

const FilterPopover = ({
  column,
  anchorEl,
  values,
  deselected,
  onToggle,
  onSetVisible,
  onClearColumn,
  onClose,
}: FilterPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const title = HISTORY_COLUMNS[column];
  const visibleValues = useMemo(
    () => values.filter((entry) => entry.value.toLowerCase().includes(term.trim().toLowerCase())),
    [term, values]
  );
  const selectedCount = visibleValues.filter((entry) => !deselected.has(entry.value)).length;
  const allChecked = visibleValues.length > 0 && selectedCount === visibleValues.length;
  const indeterminate = selectedCount > 0 && !allChecked;

  useLayoutEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return undefined;
    const place = () => {
      const rect = anchorEl.getBoundingClientRect();
      const margin = 12;
      const left = Math.min(Math.max(margin, rect.left), window.innerWidth - popover.offsetWidth - margin);
      let top = rect.bottom + 6;
      if (top + popover.offsetHeight > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - popover.offsetHeight - 6);
      }
      popover.style.left = `${left}px`;
      popover.style.top = `${top}px`;
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchorEl, term, visibleValues.length]);

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popoverRef.current?.contains(target) || anchorEl.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [anchorEl, onClose]);

  return createPortal(
    <FilterPopoverRoot
      ref={popoverRef}
      role="dialog"
      aria-label={`Filter ${title}`}
      data-testid="idv-history-filter-popover"
    >
      <FilterPopoverHead>
        <div>
          <FilterEyebrow>{HISTORY_FILTER_EYEBROW}</FilterEyebrow>
          <FilterPopoverTitle>{title}</FilterPopoverTitle>
        </div>
        <FilterClose type="button" aria-label={HISTORY_FILTER_CLOSE_LABEL} onClick={onClose}>
          ×
        </FilterClose>
      </FilterPopoverHead>
      <FilterSearch
        type="search"
        value={term}
        placeholder={HISTORY_FILTER_SEARCH_PLACEHOLDER}
        autoComplete="off"
        autoFocus
        onChange={(event) => setTerm(event.target.value)}
      />
      <FilterCheck all>
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allChecked}
          onChange={() =>
            onSetVisible(
              visibleValues.map((entry) => entry.value),
              !allChecked
            )
          }
        />
        <FilterValueLabel>{HISTORY_FILTER_SELECT_ALL}</FilterValueLabel>
      </FilterCheck>
      <FilterValues>
        {visibleValues.map((entry) => (
          <FilterCheck key={entry.value}>
            <input
              type="checkbox"
              checked={!deselected.has(entry.value)}
              onChange={(event) => onToggle(entry.value, event.target.checked)}
            />
            <FilterValueLabel>{entry.value}</FilterValueLabel>
            <FilterCount>{entry.count}</FilterCount>
          </FilterCheck>
        ))}
      </FilterValues>
      <FilterPopoverFoot>
        <FilterLink type="button" onClick={onClearColumn}>
          {HISTORY_FILTER_CLEAR_COLUMN}
        </FilterLink>
        <FilterDone type="button" onClick={onClose}>
          {HISTORY_FILTER_DONE}
        </FilterDone>
      </FilterPopoverFoot>
    </FilterPopoverRoot>,
    document.body
  );
};

export default FilterPopover;
